import { Badge, Button, Group, Modal, NumberInput, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { currencySymbol, formatMoney, toMajorUnits, toMinorUnits } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

/**
 * Closing a platform payout: what they said they were sending, and what actually landed.
 *
 * The payouts tab could record the period and the charges a platform held back and then had no way
 * to say the money arrived — so every payout sat as a draft for ever, the "actually received"
 * column was permanently blank, and nothing in the office ever noticed a platform paying short.
 * That is the entire reason anyone opens this tab, so it is a button here rather than a column.
 */

/** Everything the platform kept: commission, delivery fees, marketing contributions. */
export function heldBackMinor(row: Record<string, unknown>): number {
  const deductions = Array.isArray(row.deductions) ? row.deductions : [];
  return deductions.reduce<number>((sum, line) => {
    const amount = (line as { amountMinor?: unknown })?.amountMinor;
    return sum + (typeof amount === 'number' ? amount : 0);
  }, 0);
}

/** What should reach the bank: the sales they matched, less everything they held back. */
export function expectedNetMinor(row: Record<string, unknown>): number {
  const gross = typeof row.grossMatchedMinor === 'number' ? row.grossMatchedMinor : 0;
  return gross - heldBackMinor(row);
}

/**
 * The answer, not the state name.
 *
 * "Does not match" tells the owner to go and work out the gap himself, when the gap is the one
 * number he wanted; and a settlement nobody has recorded a payment against is not a "draft" — it
 * is money still owed.
 */
export function SettlementOutcome({ row }: { row: Record<string, unknown> }) {
  const currency = (row.currency as string) ?? 'HNL';
  const received = row.netReceivedMinor;
  if (typeof received !== 'number') {
    return (
      <Badge color="yellow" variant="light">
        Not recorded yet
      </Badge>
    );
  }
  const gap = received - expectedNetMinor(row);
  if (gap === 0) {
    return (
      <Badge color="teal" variant="light">
        Matches
      </Badge>
    );
  }
  return (
    <Badge color="red" variant="light">
      {gap < 0 ? `${formatMoney(-gap, currency)} short` : `${formatMoney(gap, currency)} over`}
    </Badge>
  );
}

export function SettlementPaymentButton({
  row,
  refresh,
}: {
  row: Record<string, unknown>;
  refresh: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const expected = expectedNetMinor(row);
  const settled = typeof row.netReceivedMinor === 'number';
  const [amount, setAmount] = useState<number | string>(
    toMajorUnits(settled ? (row.netReceivedMinor as number) : expected),
  );
  // The money this payout is in, taken from the sales it covers rather than offered as a choice
  // here. A box that let you say the dollars arrived against a statement of lempira sales rewrote
  // the whole row into dollars — "sales covered", "they held back" and all — and then printed a
  // shortfall that was really just the exchange rate.
  const currency = (row.currency as string) ?? 'HNL';
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const netReceivedMinor = toMinorUnits(amount);
    if (netReceivedMinor === undefined || !Number.isSafeInteger(netReceivedMinor)) {
      setError('Enter the bank movement: a payout, zero for no transfer, or a negative amount for a debit.');
      return;
    }
    setSaving(true);
    try {
      await rawApi('POST', `/admin/v1/channels/settlements/${String(row.id)}/reconcile`, {
        netReceivedMinor,
        currency,
      });
      const gap = netReceivedMinor - expected;
      notifyDone(
        'Bank movement recorded',
        gap === 0
          ? 'It matches what the platform owed for this period.'
          : `That is ${formatMoney(Math.abs(gap), currency)} ${gap < 0 ? 'less' : 'more'} than their own figures come to — worth querying with them.`,
      );
      setOpen(false);
      refresh();
    } catch (err) {
      notifyError(err, 'record this payout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button size="xs" variant="light" onClick={() => setOpen(true)}>
        {settled ? 'Change bank movement' : 'Record bank movement'}
      </Button>
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title="Actual payout or debit"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <Stack gap={2}>
            <Group justify="space-between">
              <Text size="sm">Sales covered</Text>
              <Text size="sm">{formatMoney(row.grossMatchedMinor as number | undefined, currency)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm">They held back</Text>
              <Text size="sm">−{formatMoney(heldBackMinor(row), currency)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                Should have arrived
              </Text>
              <Text size="sm" fw={600}>
                {formatMoney(expected, currency)}
              </Text>
            </Group>
          </Stack>
          <NumberInput
            label="Bank movement"
            description="Enter the payout received, zero when credits cancel it out, or a negative amount for a platform debit. Leave the suggested amount if the statement matches."
            value={amount}
            onChange={(v) => {
              setAmount(v);
              setError(null);
            }}
            error={error}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            prefix={`${currencySymbol(currency)} `}
            hideControls
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} loading={saving}>
              Record it
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
